import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import { SearchInput } from 'widgets-v6/standard-input';
import { SimpleSelect } from 'widgets-v6/select';

import { AudienceCard } from '../audience-card';
import { AudienceTable } from '../audience-table';
import { getEnvironmentSettings } from 'services/environment';
import { useOverallTableDashboardData } from '../../overall-dashboard/v2/hook';
import { isInternalUser } from 'states/profile/business-rules';
import { WarningText } from 'forms/ad-form/modules/cross-platform-setup-warning';

import flags from 'containers/flags/service';

const useStyles = makeStyles({
    bannerHeaderContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    bannerHeader: {
        width: '100%',
        height: '188px',
    },
});

export const CategoryDashboard = ({
    audienceSegments,
    onSearch,
    segmentCount,
    createEditAction,
    activeAudience,
    organizationId,
    sortingOptions,
    description,
    title,
    linkToAudienceTab,
    bannerImagePath,
    modal,
    tabFields,
    renderAudienceCard,
    allowExternalCreate = false,
}) => {
    const classes = useStyles();
    const { canEditAudiences } = useOverallTableDashboardData();
    const [activeSort, setActiveSort] = useState(sortingOptions[0]);
    const [activeTab, setActiveTab] = useState('all');
    const { tabs, tabValueMapping, tabValueMappingV2, valueField } = tabFields || {};
    const isUsingTabs = tabs && tabValueMapping;

    const handleChangeTab = newValue => {
        setActiveTab(newValue);
    };

    const debouncedSearch = useMemo(() => {
        return _.debounce(value => onSearch(value), 200);
    }, []);

    const handleSearch = e => {
        const { value } = e.target;
        debouncedSearch(value);
    };

    const handleSortChange = event => {
        const { value, direction } = _.find(sortingOptions, { value: event.target.value });
        setActiveSort({ value, direction });
    };

    const sortedAudiences = _.orderBy(audienceSegments, [activeSort.value], [activeSort.direction]);

    const filteredAudiencesByTab = isUsingTabs
        ? _.filter(sortedAudiences, audience => {
              if (flags.isEnabled('en_2987_use_audience_category_and_subCategory_code')) {
                  const activeSubCategories = tabValueMappingV2[activeTab].value;
                  return _.includes(activeSubCategories, audience.subCategory);
              }
              const activeSourceTypes = tabValueMapping[activeTab].value;
              return _.includes(activeSourceTypes, audience[valueField]);
          })
        : sortedAudiences;

    const environmentSettings = getEnvironmentSettings();

    const canCreate = isInternalUser() || allowExternalCreate;
    return (
        <Box padding={4}>
            <Container>
                <Breadcrumbs aria-label="breadcrumb">
                    <RouterLink
                        color="inherit"
                        component={Link}
                        to={linkToAudienceTab}
                        underline="none"
                    >
                        {linkToAudienceTab.linkBackLabel}
                    </RouterLink>
                    <Typography color="textPrimary"> {title}</Typography>
                </Breadcrumbs>
                <br />
                <br />
                <Grid container>
                    <div className={classes.bannerHeaderContainer}>
                        <img src={bannerImagePath} className={classes.bannerHeader} />
                        <br />
                        <br />
                        <Typography variant="h5" color="textSecondary" gutterBottom>
                            {description}
                        </Typography>
                        <br />
                        <Typography variant="h5">{segmentCount}</Typography>
                        <Typography variant="subtitle2" color="textSecondary">
                            Total
                        </Typography>
                    </div>
                    <Grid item xs={8}>
                        {canEditAudiences &&
                            environmentSettings.canUseAudienceBuilder() &&
                            createEditAction && (
                                <Box mt={1}>
                                    <Tooltip
                                        title={
                                            !canCreate ? (
                                                <Typography variant="body2">
                                                    Please reach out to your dedicated Customer
                                                    Engagement Specialist or Client Partner to
                                                    create a Custom Audience!
                                                </Typography>
                                            ) : (
                                                ''
                                            )
                                        }
                                    >
                                        <Box display="inline-block">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => createEditAction()}
                                                disabled={!canCreate}
                                            >
                                                <AddIcon /> New Audience
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                    {!canCreate && (
                                        <Box mt={1}>
                                            <WarningText
                                                severity="medium"
                                                message="Please reach out to your dedicated Customer Engagement Specialist or Client Partner to create a Custom Audience!"
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}
                    </Grid>
                </Grid>
                {isUsingTabs && (
                    <Box mt={5} mb={1.5}>
                        <Grid container spacing={1}>
                            {_.map(tabs, option => (
                                <Grid item key={option.value}>
                                    <Chip
                                        label={option.label}
                                        avatar={option.avatar}
                                        color={activeTab === option.value ? 'primary' : 'default'}
                                        onClick={() => handleChangeTab(option.value)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
                <Box mb={2.5} mt={5}>
                    <Grid container justifyContent="space-between">
                        <Grid item xs={3}>
                            <SearchInput placeholder="Filter Audiences" onChange={handleSearch} />
                        </Grid>
                        <Grid item xs={3}>
                            <SimpleSelect
                                options={sortingOptions}
                                value={activeSort.value}
                                onChange={handleSortChange}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <AudienceTable
                    audiences={isUsingTabs ? filteredAudiencesByTab : sortedAudiences}
                    cardSize="large"
                    activeAudience={activeAudience}
                    renderContent={
                        renderAudienceCard
                            ? renderAudienceCard
                            : segment => (
                                  <AudienceCard
                                      segment={segment}
                                      active={segment.id === activeAudience}
                                      organizationId={organizationId}
                                  />
                              )
                    }
                />
                {modal}
            </Container>
        </Box>
    );
};

export default CategoryDashboard;
